// Anke, 2026-07-21 — "es gibt offenbar 2 Zwischenablagen, das ist nicht
// gewünscht."
//
// Sie hatte recht, wenn auch anders als es aussah: Kopieren UND Einfügen liefen
// schon über dieselben Funktionen, aber ausschliesslich über eine board-eigene
// Ablage im Arbeitsspeicher. Die Rechner-Zwischenablage wurde nie gelesen — wer
// woanders Text kopierte und aufs Board einfügte, bekam wieder das ALTE Objekt.
// Genau das fühlt sich nach zwei Ablagen an.
//
// Damit Board-Objekte durch die echte Zwischenablage passen, brauchen sie eine
// Textdarstellung. Sie wandert als `text/plain` hinaus, mit einer Kennung davor,
// an der das Board sein eigenes Zeug beim Einfügen wiedererkennt.
//
// `text/plain` und kein eigener Datentyp: Eigene Zwischenablage-Formate sind
// browserabhängig und über `navigator.clipboard` nur eingeschränkt zu schreiben.
// Der Preis ist, dass ein in eine Mail eingefügtes Objekt als Datenblock
// erscheint — mit Anke besprochen und bewusst so gewählt: Dafür funktioniert
// Kopieren zwischen zwei Boards und zwei Browser-Tabs, und das war der Punkt.

import type { BoardObject } from '../interfaces/board-object.interface.js';

/**
 * Kennung am Anfang der Nutzlast. Enthält eine Version, damit ein späteres
 * Format erkennbar bleibt statt beim Einfügen als Fliesstext zu landen.
 */
export const CLIPBOARD_MARKER = 'o2d-whiteboard/v1:';

/** Board-Objekte in die Textform bringen, die auf der Zwischenablage landet. */
export function encodeClipboardPayload(objects: ReadonlyArray<BoardObject>): string {
  return CLIPBOARD_MARKER + JSON.stringify(objects);
}

/**
 * Board-Objekte aus einem Zwischenablage-Text zurücklesen, oder `null`, wenn der
 * Text nicht von uns stammt oder beschädigt ist.
 *
 * Bewusst `null` statt einer Ausnahme: Der Aufrufer behandelt "keine
 * Board-Objekte" ohnehin als regulären Fall — dann ist es eben fremder Text, aus
 * dem eine Textbox wird. Beschädigte eigene Daten (abgeschnitten beim Kopieren,
 * von Hand verändert) laufen in denselben Zweig; eine halb gelesene Objektliste
 * einzufügen wäre schlimmer als sie zu verwerfen.
 */
export function decodeClipboardPayload(text: string): ReadonlyArray<BoardObject> | null {
  if (!text.startsWith(CLIPBOARD_MARKER)) return null;
  try {
    const parsed: unknown = JSON.parse(text.slice(CLIPBOARD_MARKER.length));
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Nur oberflächlich prüfen: Jedes Element braucht die Felder, ohne die der
    // Renderer sofort stolpern würde. Eine vollständige Prüfung gehört ins
    // Schema, nicht in die Zwischenablage.
    const looksSane = parsed.every(
      (o) =>
        typeof o === 'object' && o !== null &&
        typeof (o as BoardObject).type === 'string' &&
        typeof (o as BoardObject).x === 'number' &&
        typeof (o as BoardObject).y === 'number',
    );
    return looksSane ? (parsed as ReadonlyArray<BoardObject>) : null;
  } catch {
    return null;
  }
}
